import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Toaster } from "sonner";
import { persistOptions } from "./lib/queryPersist";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminAuthProvider, useAdminAuth } from "./context/AdminAuthContext";
import { EmployeeAuthProvider, useEmployeeAuth } from "./context/EmployeeAuthContext";
import { ThemeModeProvider, useThemeMode } from "./context/ThemeModeContext";
import { useAppTheme } from "./hooks/useAppTheme";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RestrictedForPendingMember } from "./components/RestrictedForPendingMember";
import { InstallPwaPrompt } from "./components/InstallPwaPrompt";

// 2026-09-14 perf fix: every route page used to be imported eagerly, so a first-ever
// WebView launch had to download+parse+compile ALL ~80 pages (member + admin + checkout)
// in one giant bundle before anything could render - the dominant cause of the reported
// slow first load (subsequent loads were fast only because the WebView's V8 code cache and
// HTTP cache were already warm from that first heavy parse/compile). React.lazy splits each
// route into its own chunk, so a cold boot only ever downloads/parses the ONE page actually
// being visited; every other page loads on-demand the first time it's navigated to.
const AuthFlow = lazy(() => import("./pages/AuthFlow"));
const LegalContentPage = lazy(() => import("./pages/LegalContentPage"));
const CashfreeRedirectPage = lazy(() => import("./pages/CashfreeRedirectPage"));
const CashfreeReturnPage = lazy(() => import("./pages/CashfreeReturnPage"));
const AppDownloadPage = lazy(() => import("./pages/AppDownloadPage"));
const CampaignLandingPage = lazy(() => import("./pages/CampaignLandingPage"));
const ArCardPage = lazy(() => import("./pages/ArCardPage"));
const DetailedBenefitsPage = lazy(() => import("./pages/DetailedBenefitsPage"));
const CheckoutFlow = lazy(() => import("./pages/CheckoutFlow"));
const HealthCheckoutFlow = lazy(() => import("./pages/HealthCheckoutFlow"));
const RepurchaseFlow = lazy(() => import("./pages/RepurchaseFlow"));
const MemberLayout = lazy(() => import("./pages/member/MemberLayout"));
const DashboardOverview = lazy(() => import("./pages/member/DashboardOverview"));
const MyProfilePage = lazy(() => import("./pages/member/MyProfilePage"));
const MyPackagePage = lazy(() => import("./pages/member/MyPackagePage"));
const BatchPage = lazy(() => import("./pages/member/BatchPage"));
const ReferralPage = lazy(() => import("./pages/member/ReferralPage"));
const RebirthPage = lazy(() => import("./pages/member/RebirthPage"));
const BoosterPoolPage = lazy(() => import("./pages/member/BoosterPoolPage"));
const RankLeaderboard = lazy(() => import("./pages/member/RankLeaderboard"));
const RepurchasesPage = lazy(() => import("./pages/member/RepurchasesPage"));
const WalletPage = lazy(() => import("./pages/member/WalletPage"));
const WithdrawPage = lazy(() => import("./pages/member/WithdrawPage"));
const BonusesPage = lazy(() => import("./pages/member/BonusesPage"));
const NotificationsPage = lazy(() => import("./pages/member/NotificationsPage"));
const SupportPage = lazy(() => import("./pages/member/SupportPage"));
const SettingsPage = lazy(() => import("./pages/member/SettingsPage"));
const TeamTreePage = lazy(() => import("./pages/member/TeamTreePage"));
const MyVisitingCardPage = lazy(() => import("./pages/member/MyVisitingCardPage"));
const MemberFinancialAnalytics = lazy(() => import("./pages/member/MemberFinancialAnalytics"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const EmployeeLogin = lazy(() => import("./pages/employee/EmployeeLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminMembers = lazy(() => import("./pages/admin/AdminMembers"));
const AdminMemberDetail = lazy(() => import("./pages/admin/AdminMemberDetail"));
const AdminPins = lazy(() => import("./pages/admin/AdminPins"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminIncomeSettings = lazy(() => import("./pages/admin/AdminIncomeSettings"));
const AdminBatchIncome = lazy(() => import("./pages/admin/AdminBatchIncome"));
const AdminRebirthManagement = lazy(() => import("./pages/admin/AdminRebirthManagement"));
const AdminWalletManagement = lazy(() => import("./pages/admin/AdminWalletManagement"));
const AdminWebhooks = lazy(() => import("./pages/admin/AdminWebhooks"));
const AdminBonusCampaigns = lazy(() => import("./pages/admin/AdminBonusCampaigns"));
const AdminSupportTickets = lazy(() => import("./pages/admin/AdminSupportTickets"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminPackages = lazy(() => import("./pages/admin/AdminPackages"));
const AdminRegistrationFields = lazy(() => import("./pages/admin/AdminRegistrationFields"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminDirectCollectionPins = lazy(() => import("./pages/admin/AdminDirectCollectionPins"));
const AdminScratchCard = lazy(() => import("./pages/admin/AdminScratchCard"));
const ScratchCardPage = lazy(() => import("./pages/member/ScratchCardPage"));
const AdminIntegrations = lazy(() => import("./pages/admin/AdminIntegrations"));
const AdminPendingActivations = lazy(() => import("./pages/admin/AdminPendingActivations"));
const AdminRpIdentities = lazy(() => import("./pages/admin/AdminRpIdentities"));
const AdminBatchQueueMonitor = lazy(() => import("./pages/admin/AdminBatchQueueMonitor"));
const AdminRankManagement = lazy(() => import("./pages/admin/AdminRankManagement"));
const AdminBoosterPool = lazy(() => import("./pages/admin/AdminBoosterPool"));
const AdminEmployeeManagement = lazy(() => import("./pages/admin/AdminEmployeeManagement"));
const AdminFinancialLedger = lazy(() => import("./pages/admin/AdminFinancialLedger"));
const AdminMemberFinancialAnalytics = lazy(() => import("./pages/admin/AdminMemberFinancialAnalytics"));
const AdminMemberTypes = lazy(() => import("./pages/admin/AdminMemberTypes"));
const AdminAutomation = lazy(() => import("./pages/admin/AdminAutomation"));
const AdminFinancialReconciliation = lazy(() => import("./pages/admin/AdminFinancialReconciliation"));
const AdminQueueHealth = lazy(() => import("./pages/admin/AdminQueueHealth"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminAccounting = lazy(() => import("./pages/admin/AdminAccounting"));
const AdminTermsAudit = lazy(() => import("./pages/admin/AdminTermsAudit"));
const AdminBankVerification = lazy(() => import("./pages/admin/AdminBankVerification"));
const AdminBulkpe = lazy(() => import("./pages/admin/AdminBulkpe"));
const AdminAchievementPosters = lazy(() => import("./pages/admin/AdminAchievementPosters"));
const AdminRepurchase = lazy(() => import("./pages/admin/AdminRepurchase"));
const AdminNotificationRules = lazy(() => import("./pages/admin/AdminNotificationRules"));
const AdminBackupRestore = lazy(() => import("./pages/admin/AdminBackupRestore"));
const AdminSystemDocumentation = lazy(() => import("./pages/admin/AdminSystemDocumentation"));
const AdminCampaignPages = lazy(() => import("./pages/admin/AdminCampaignPages"));
const AdminStories = lazy(() => import("./pages/admin/AdminStories"));
const AdminCampaignPageEditor = lazy(() => import("./pages/admin/AdminCampaignPageEditor"));
const AdminCampaignPreview = lazy(() => import("./pages/admin/AdminCampaignPreview"));
const AdminCampaignAnalytics = lazy(() => import("./pages/admin/AdminCampaignAnalytics"));
const AdminSecurityPage = lazy(() => import("./pages/admin/AdminSecurityPage"));
const AdminKnowledgeBase = lazy(() => import("./pages/admin/AdminKnowledgeBase"));
const AdminAIAssistant = lazy(() => import("./pages/admin/AdminAIAssistant"));
const AdminTeamTree = lazy(() => import("./pages/admin/AdminTeamTree"));
const AdminTeamBooster = lazy(() => import("./pages/admin/AdminTeamBooster"));
const AdminArCard = lazy(() => import("./pages/admin/AdminArCard"));
const HelpCenterPage = lazy(() => import("./pages/member/HelpCenterPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000, // default for any future query without its own override - avoids
      // refetching on every single mount; financial/member-specific data should still set
      // its own explicit (short or zero) staleTime rather than relying on this default.
      gcTime: 5 * 60_000,
    },
  },
});

function RouteLoadingFallback() {
  return <div className="min-h-screen bg-bg flex items-center justify-center text-slate-400">Loading...</div>;
}

function MemberPrivateRoute({ children, fallback = "/" }) {
  const { member, loading } = useAuth();
  if (loading) return <RouteLoadingFallback />;
  if (!member) return <Navigate to={fallback} replace />;
  return children;
}

// Root ("/") route (2026-08-21 fix): a member whose JWT is still valid on cold app start
// was being shown the login/registration wizard again instead of the Dashboard - the token
// WAS being restored correctly (see AuthContext), there was just no redirect away from "/"
// once `member` loaded. This is exactly what made the Android app "ask to login every time".
function RootRoute() {
  const { member, loading } = useAuth();
  if (loading) return <RouteLoadingFallback />;
  if (member) return <Navigate to="/dashboard" replace />;
  return <AuthFlow />;
}

// Dedicated Vijayamarga Health Product signup entry point (2026-08-23) - accessible ONLY
// via this direct URL, never linked from the homepage/normal signup/nav.
function HealthRootRoute() {
  const { member, loading } = useAuth();
  if (loading) return <RouteLoadingFallback />;
  if (member) return <Navigate to="/dashboard" replace />;
  return <AuthFlow flowMode="health" />;
}

function AdminPrivateRoute({ children }) {
  const { admin } = useAdminAuth();
  const { employee } = useEmployeeAuth();
  if (!admin && !employee) return <Navigate to="/admin/login" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  const { admin } = useAdminAuth();
  if (!admin) return <Navigate to="/admin/overview" replace />;
  return children;
}

function ThemedToaster() {
  const ctx = useThemeMode();
  return <Toaster theme={ctx?.mode === "light" ? "light" : "dark"} position="top-center" richColors />;
}

export default function App() {
  useAppTheme();
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <BrowserRouter>
        <AuthProvider>
          <AdminAuthProvider>
            <EmployeeAuthProvider>
            <ThemeModeProvider>
            <ThemedToaster />
            <InstallPwaPrompt />
            <ErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/legal/:slug" element={<LegalContentPage />} />
              <Route path="/pay/cashfree-redirect" element={<CashfreeRedirectPage />} />
              <Route path="/pay/cashfree-return" element={<CashfreeReturnPage />} />
              <Route path="/app-download" element={<AppDownloadPage />} />
              <Route path="/campaign/:slug" element={<CampaignLandingPage />} />
              <Route path="/campaign/:slug/benefits" element={<DetailedBenefitsPage />} />
              <Route path="/ar-card" element={<ArCardPage />} />
              <Route path="/admin/campaign-pages/:campaignId/preview" element={<AdminOnlyRoute><AdminCampaignPreview /></AdminOnlyRoute>} />
              <Route path="/health-products/signup" element={<HealthRootRoute />} />
              <Route
                path="/checkout"
                element={
                  <MemberPrivateRoute>
                    <CheckoutFlow />
                  </MemberPrivateRoute>
                }
              />
              <Route
                path="/health-checkout"
                element={
                  <MemberPrivateRoute fallback="/health-products/signup">
                    <HealthCheckoutFlow />
                  </MemberPrivateRoute>
                }
              />
              <Route
                path="/repurchase"
                element={
                  <MemberPrivateRoute>
                    <RepurchaseFlow />
                  </MemberPrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <MemberPrivateRoute>
                    <MemberLayout />
                  </MemberPrivateRoute>
                }
              >
                <Route index element={<DashboardOverview />} />
                <Route path="profile" element={<RestrictedForPendingMember><MyProfilePage /></RestrictedForPendingMember>} />
                <Route path="package" element={<MyPackagePage />} />
                <Route path="batch" element={<RestrictedForPendingMember><BatchPage /></RestrictedForPendingMember>} />
                <Route path="referral" element={<RestrictedForPendingMember><ReferralPage /></RestrictedForPendingMember>} />
                <Route path="rebirth" element={<RestrictedForPendingMember><RebirthPage /></RestrictedForPendingMember>} />
                <Route path="booster-pool" element={<RestrictedForPendingMember><BoosterPoolPage /></RestrictedForPendingMember>} />
                <Route path="scratch-card" element={<RestrictedForPendingMember><ScratchCardPage /></RestrictedForPendingMember>} />
                <Route path="leaderboard" element={<RestrictedForPendingMember><RankLeaderboard /></RestrictedForPendingMember>} />
                <Route path="repurchases" element={<RestrictedForPendingMember><RepurchasesPage /></RestrictedForPendingMember>} />
                <Route path="team-tree" element={<RestrictedForPendingMember><TeamTreePage /></RestrictedForPendingMember>} />
                <Route path="visiting-card" element={<RestrictedForPendingMember><MyVisitingCardPage /></RestrictedForPendingMember>} />
                <Route path="financial-analytics" element={<RestrictedForPendingMember><MemberFinancialAnalytics /></RestrictedForPendingMember>} />
                <Route path="wallet" element={<RestrictedForPendingMember><WalletPage /></RestrictedForPendingMember>} />
                <Route path="withdraw" element={<RestrictedForPendingMember><WithdrawPage /></RestrictedForPendingMember>} />
                <Route path="bonuses" element={<RestrictedForPendingMember><BonusesPage /></RestrictedForPendingMember>} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="help" element={<HelpCenterPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/employee/login" element={<EmployeeLogin />} />
              <Route
                path="/admin"
                element={
                  <AdminPrivateRoute>
                    <AdminLayout />
                  </AdminPrivateRoute>
                }
              >
                <Route index element={<Navigate to="/admin/overview" replace />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="members" element={<AdminMembers />} />
                <Route path="members/:id" element={<AdminMemberDetail />} />
                <Route path="pins" element={<AdminPins />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="batch-income" element={<AdminBatchIncome />} />
                <Route path="batch-queue-monitor" element={<AdminBatchQueueMonitor />} />
                <Route path="rank-management" element={<AdminRankManagement />} />
                <Route path="member-types" element={<AdminMemberTypes />} />
                <Route path="automation" element={<AdminAutomation />} />
                <Route path="booster-pool" element={<AdminBoosterPool />} />
                <Route path="team-booster" element={<AdminTeamBooster />} />
                <Route path="ar-card" element={<AdminOnlyRoute><AdminArCard /></AdminOnlyRoute>} />
                <Route path="team-tree" element={<AdminTeamTree />} />
                <Route path="rebirth" element={<AdminRebirthManagement />} />
                <Route path="wallet" element={<AdminWalletManagement />} />
                <Route path="bonus" element={<AdminBonusCampaigns />} />
                <Route path="tickets" element={<AdminSupportTickets />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="packages" element={<AdminPackages />} />
                <Route path="registration-fields" element={<AdminRegistrationFields />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="direct-collection-pins" element={<AdminDirectCollectionPins />} />
                <Route path="scratch-card" element={<AdminScratchCard />} />
                <Route path="financial-ledger" element={<AdminFinancialLedger />} />
                <Route path="member-financial-analytics" element={<AdminMemberFinancialAnalytics />} />
                <Route path="financial-reconciliation" element={<AdminOnlyRoute><AdminFinancialReconciliation /></AdminOnlyRoute>} />
                <Route path="queue-health" element={<AdminOnlyRoute><AdminQueueHealth /></AdminOnlyRoute>} />
                <Route path="invoices" element={<AdminInvoices />} />
             <Route path="accounting" element={<AdminAccounting />} />
             <Route path="terms-audit" element={<AdminTermsAudit />} />
                <Route path="bank-verification" element={<AdminBankVerification />} />
                <Route path="bulkpe" element={<AdminBulkpe />} />
                <Route path="achievement-posters" element={<AdminAchievementPosters />} />
                <Route path="notifications" element={<AdminNotificationRules />} />
                <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
                <Route path="ai-assistant" element={<AdminAIAssistant />} />
                <Route path="backup" element={<AdminOnlyRoute><AdminBackupRestore /></AdminOnlyRoute>} />
                <Route path="system-documentation" element={<AdminOnlyRoute><AdminSystemDocumentation /></AdminOnlyRoute>} />
                <Route path="campaign-pages" element={<AdminOnlyRoute><AdminCampaignPages /></AdminOnlyRoute>} />
                <Route path="campaign-pages/analytics" element={<AdminOnlyRoute><AdminCampaignAnalytics /></AdminOnlyRoute>} />
                <Route path="campaign-pages/:campaignId/analytics" element={<AdminOnlyRoute><AdminCampaignAnalytics /></AdminOnlyRoute>} />
                <Route path="campaign-pages/:campaignId" element={<AdminOnlyRoute><AdminCampaignPageEditor /></AdminOnlyRoute>} />
                <Route path="stories" element={<AdminStories />} />
                <Route path="security" element={<AdminSecurityPage />} />
                <Route path="integrations" element={<AdminIntegrations />} />
                <Route path="pending-activations" element={<AdminPendingActivations />} />
                <Route path="repurchase" element={<AdminRepurchase />} />
                <Route path="rp-identities" element={<AdminRpIdentities />} />
                <Route path="webhooks" element={<AdminWebhooks />} />
                <Route path="income-settings" element={<AdminIncomeSettings />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="audit" element={<AdminAuditLogs />} />
                <Route path="employees" element={<AdminOnlyRoute><AdminEmployeeManagement /></AdminOnlyRoute>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
            </ThemeModeProvider>
            </EmployeeAuthProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  );
}
